// eslint-disable-next-line no-unused-vars
import React, {useState} from 'react';
import styled from 'styled-components';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {FaTicketAlt} from 'react-icons/fa';
import {redeemCoupon, logoutUser} from '../../../api/axios';
import {useAuth} from '../../../hooks/useAuth';
import ResultModal from '../modal/resultModal';
import {useTranslation} from 'react-i18next';
import i18n from '../../../i18n';
import { Navigate} from 'react-router-dom';

const CouponForm = styled.form`
  width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 10px;
`;

const StyledInputField = styled.input`
  padding: 10px 10px 10px 40px; /* Extra left padding for the icon */
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
  text-transform: uppercase; /* Force uppercase */
  @media (max-width: 768px) {
    width: 300px;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  color: #aaa;
  pointer-events: none;
`;

const ErrorText = styled.p`
  color: red;
  margin: 0 0 10px 0;
  font-size: 0.9em;
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  background-color: #0077cc;
  color: #fff;
  cursor: pointer;
`;

const couponSchema = yup.object().shape({
  couponCode: yup
    .string()
    .required(i18n.t('couponSchema.required'))
    .min(5, i18n.t('couponSchema.min'))
    .max(30, i18n.t('couponSchema.max')),
});

const FAILED_ATTEMPTS_THRESHOLD = 10;

const CouponRedeem = () => {
  const {t} = useTranslation();
  const {user, refreshUserData} = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    formState: {errors},
  } = useForm({
    resolver: yupResolver(couponSchema),
  });

  // Modal state
  const [modalShow, setModalShow] = useState(false);
  const [modalSuccess, setModalSuccess] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  // Count failed coupon attempts
  const [failedAttempts, setFailedAttempts] = useState(0);

  const onSubmit = async data => {
    try {
      const response = await redeemCoupon(data.couponCode);
      if (response.data && response.data.success) {
        setModalSuccess(true);
        setModalMessage(response.data.message || t('couponRedeem.success'));
        refreshUserData();
        setFailedAttempts(0);
      } else {
        setFailedAttempts(prev => prev + 1);
        setModalSuccess(false);
        setModalMessage(response.data.message || t('couponRedeem.error'));
        if (failedAttempts + 1 >= FAILED_ATTEMPTS_THRESHOLD) {
          setModalMessage(t('couponRedeem.tooManyAttempts'));
          setTimeout(() => {
            logoutUser();
          }, 3000);
        }
      }
      setModalShow(true);
      reset();
    } catch (error) {
      setFailedAttempts(prev => prev + 1);
      setModalSuccess(false);
      setModalMessage(t('couponRedeem.error') + ': ' + (error.message || t('couponRedeem.unknownError')));
      if (failedAttempts + 1 >= FAILED_ATTEMPTS_THRESHOLD) {
        setModalMessage(t('couponRedeem.tooManyAttempts'));
        setTimeout(() => {
          logoutUser();
        }, 3000);
      }
      setModalShow(true);
    }
  };

    if (!user) {
      return <Navigate to="/" replace />;
    }

  return (
    <div>
      <p>{t('couponRedeem.enterCoupon')}</p>
      <CouponForm onSubmit={handleSubmit(onSubmit)}>
        <InputWrapper>
          <IconWrapper>
            <FaTicketAlt size={20} />
          </IconWrapper>
          <StyledInputField
            type="text"
            {...register('couponCode', {
              setValueAs: value => value.toUpperCase(),
            })}
            placeholder={t('couponRedeem.couponPlaceholder')}
            autoComplete="off"
          />
        </InputWrapper>
        {errors.couponCode && <ErrorText>{errors.couponCode.message}</ErrorText>}
        <SubmitButton type="submit">{t('couponRedeem.redeemButton')}</SubmitButton>
      </CouponForm>
      <ResultModal show={modalShow} onHide={() => setModalShow(false)} success={modalSuccess} message={modalMessage} />
    </div>
  );
};

export default CouponRedeem;
